import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  changeCompanyRequest,
  changeToOnboardingRequest,
  impersonateEmployeeRequest,
  impersonateRequest,
  loginRequest,
  ssoLoginRequest,
  validateTokenRequest,
} from 'api/requests/auth-requests';
import localStorageService from 'services/local-storage-service';
import loadProfile from 'services/post-auth-startup/load-profile';
import store from 'store';
import { MFA_ERROR_KEY_PREFIX } from 'constants/mfa';
import {
  AUTH__CHANGE_COMPANIES,
  AUTH__CHANGE_COMPANIES_AS_EMPLOYEE,
  AUTH__CHANGE_TO_ONBOARDING,
  AUTH__GENERAL_LOADING,
  AUTH__IMPERSONATE,
  AUTH__IMPERSONATE_EMPLOYEE,
  AUTH__LOGIN,
  AUTH__LOGOUT,
  AUTH__SET_ERROR,
  AUTH__SET_MFA_ERROR_KEY,
  AUTH__SET_PASSWORD_TOKEN_VALIDITY,
  AUTH__SET_PASSWORD_TOKEN_VALIDITY_RESULT,
  AUTH__SET_TEXT_ERROR,
  AUTH__SET_TOKEN,
  AUTH__SSO_LOGIN,
  AUTH__STOP_IMPERSONATE,
} from 'store/constants';
import ILoginRequest from 'types/auth/ILoginRequest';
import { setHasSelectedCompany } from './profile-actions';

export const setAuthRequestsLoadingAction = createAction<boolean>(AUTH__GENERAL_LOADING);

export const setAuthTokenAction = createAction<string>(AUTH__SET_TOKEN);
export const setAuthErrorAction = createAction<boolean>(AUTH__SET_ERROR);
export const setAuthMfaErrorKeyAction = createAction<string>(AUTH__SET_MFA_ERROR_KEY);

export const loginAction = createAsyncThunk(AUTH__LOGIN, async (data: ILoginRequest, thunkApi) => {
  thunkApi.dispatch(setAuthRequestsLoadingAction(true));
  thunkApi.dispatch(setAuthErrorAction(false));
  thunkApi.dispatch(setAuthMfaErrorKeyAction(''));
  try {
    const response = await loginRequest(data);
    // A user who must enrol, or answer a challenge, gets no session token — `token` is null. Nothing
    // may reach the reducer or localStorage here, so authStateSelector stays false and Router keeps
    // rendering AuthRouter. Without this branch the response falls through to `return null` below and
    // Auth.tsx sees nothing to route on: the user clicks Sign in and the screen does not move.
    if (response?.requiresMfaEnrollment || response?.requiresMfaChallenge) {
      thunkApi.dispatch(setAuthRequestsLoadingAction(false));
      return response;
    }
    if (response?.token && !response?.needsReset) {
      if (!response.hasMultifactor) {
        thunkApi.dispatch(setAuthTokenAction(response.token));
        localStorageService.setItem({ authToken: response.token });
      }
      thunkApi.dispatch(setAuthRequestsLoadingAction(false));
      return response;
    }
    if (response?.needsReset) {
      thunkApi.dispatch(setAuthRequestsLoadingAction(false));
      return response;
    }
  } catch (error) {
    // /Auth/login answers 401 [MFA] SESSION_EXPIRED when an enrollment is removed in the instant
    // between the password check and the challenge opening. That is not a bad password, so the
    // credential error stays off — otherwise the form blames the user for something they cannot fix.
    // The key is displayed as-is; the next attempt routes them to enrollment.
    const responseErrorKey = (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
    if (responseErrorKey.startsWith(MFA_ERROR_KEY_PREFIX)) {
      thunkApi.dispatch(setAuthMfaErrorKeyAction(responseErrorKey));
    } else {
      thunkApi.dispatch(setAuthErrorAction(true));
    }
  }

  thunkApi.dispatch(setAuthRequestsLoadingAction(false));
  return null;
});

export const setAuthErrorTextAction = createAction<string>(AUTH__SET_TEXT_ERROR);
export const ssoLoginAction = createAsyncThunk(AUTH__SSO_LOGIN, async (data: ILoginRequest, thunkApi) => {
  thunkApi.dispatch(setAuthRequestsLoadingAction(true));
  thunkApi.dispatch(setAuthErrorAction(false));

  try {
    const response = await ssoLoginRequest(data);
    if (response?.token) {
      thunkApi.dispatch(setAuthTokenAction(response.token));

      localStorageService.setItem({ authToken: response.token });
    }
  } catch (error) {
    thunkApi.dispatch(setAuthErrorAction(true));
    thunkApi.dispatch(setAuthErrorTextAction(error.toString()));
  }

  thunkApi.dispatch(setAuthRequestsLoadingAction(false));
});

export const logoutAction = createAsyncThunk(AUTH__LOGOUT, async (withRedirect: boolean, thunkApi) => {
  try {
    thunkApi.dispatch(setAuthTokenAction(null));
    localStorageService.removeItem('authToken');
    localStorageService.removeItem('impersonationToken');
    localStorageService.removeItem('selectedCompanyId');
    if (withRedirect) {
      window.location.href = '/';
    }
  } catch (err) {
    // swallow exception
  }
});

export const impersonateAction = createAsyncThunk(
  AUTH__IMPERSONATE,
  async (props: { userId: number; companyId: number }) => {
    try {
      const response = await impersonateRequest(props.userId, props.companyId);
      if (response?.token) {
        localStorageService.setItem({ impersonationToken: response.token });
        localStorageService.setItem({ isImpersonated: true });
        window.location.href = '/';
      }
    } catch (err) {
      // swallow exception
    }
  },
);

export const impersonateEmployeeAction = createAsyncThunk(
  AUTH__IMPERSONATE_EMPLOYEE,
  async (props: { employeeId: number; companyId: number }) => {
    try {
      const response = await impersonateEmployeeRequest(props.employeeId, props.companyId);
      if (response?.token) {
        localStorageService.setItem({ impersonationToken: response.token });
        localStorageService.setItem({ isImpersonated: true });
        window.location.href = '/';
      }
    } catch (err) {
      // swallow exception
    }
  },
);

export const stopImpersonateAction = createAsyncThunk(AUTH__STOP_IMPERSONATE, async (__, thunkApi) => {
  try {
    const token = localStorageService.getItem<string>('authToken');
    thunkApi.dispatch(setAuthTokenAction(token));
    localStorageService.removeItem('impersonationToken');
    localStorageService.removeItem('isImpersonated');
    localStorageService.removeItem('selectedCompanyId');
    window.location.href = '/';
  } catch (err) {
    // swallow exception
  }
});

export const changeCompaniesAction = createAsyncThunk(AUTH__CHANGE_COMPANIES, async (companyId: number) => {
  try {
    const response = await changeCompanyRequest(companyId);
    if (response?.token) {
      localStorageService.setItem({ impersonationToken: response.token });
      window.location.href = '/';
    }
  } catch (err) {
    // swallow exception
  }
});

export const selectCompanyForMCEmployeeAction = createAsyncThunk(
  AUTH__CHANGE_COMPANIES_AS_EMPLOYEE,
  async (companyId: number, thunkApi) => {
    try {
      const response = await changeCompanyRequest(companyId);
      if (response?.token) {
        thunkApi.dispatch(setAuthTokenAction(response.token));
        const isImpersonated = localStorageService.getItem<boolean>('isImpersonated');
        if (isImpersonated) {
          localStorageService.setItem({ impersonationToken: response.token });
        } else {
          localStorageService.setItem({ authToken: response.token });
        }
        localStorageService.setItem({ selectedCompanyId: companyId });
        thunkApi.dispatch(setHasSelectedCompany(true));
        await store.dispatch(loadProfile);
      }
    } catch (err) {
      // swallow exception
    }
  },
);

export const changeToOnboardingCompanyAction = createAsyncThunk(
  AUTH__CHANGE_TO_ONBOARDING,
  async (companyIdNumber: number, thunkApi) => {
    thunkApi.dispatch(setAuthRequestsLoadingAction(true));
    try {
      const response = await changeToOnboardingRequest(companyIdNumber);
      if (response?.token) {
        localStorageService.setItem({ impersonationToken: response.token });
        window.location.href = '/';
      }
    } catch (err) {
      // swallow exception
    }
    thunkApi.dispatch(setAuthRequestsLoadingAction(false));
  },
);

export const setPasswordValidityResultAction = createAction<boolean>(AUTH__SET_PASSWORD_TOKEN_VALIDITY_RESULT);
export const passwordTokenValidityAction = createAsyncThunk(
  AUTH__SET_PASSWORD_TOKEN_VALIDITY,
  async (token: string, thunkApi) => {
    thunkApi.dispatch(setAuthRequestsLoadingAction(true));
    try {
      const res = await validateTokenRequest(token);
      thunkApi.dispatch(setPasswordValidityResultAction(res));
    } catch (err) {
      thunkApi.dispatch(setPasswordValidityResultAction(false));
    }
    thunkApi.dispatch(setAuthRequestsLoadingAction(false));
  },
);
